

import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  ZoomControl,
  useMapEvent,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { Mail, Phone } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { FaPaperclip, FaPaperPlane } from "react-icons/fa";

const useFadeInOnScroll = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return [ref, visible];
};


const Contact = () => {

const [qrSize, setQrSize] = useState(
  window.innerWidth < 640 ? 70 : 140
);

 const [containerRef, isVisible] = useFadeInOnScroll();

useEffect(() => {
  const handleResize = () => {
    setQrSize(window.innerWidth < 640 ? 70 : 140);
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);


  const defaultPosition = [23.215277244100708, 77.40176374396313];

  const placeInfo = {
    name: "MANIT Bhopal",
    rating: 4.5,
    address: "Bhopal, MP, India",
    directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=23.2132,77.3963`,
  };

  const customIcon = new L.Icon({
    iconUrl: markerIconPng,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  const vCardData = `BEGIN:VCARD
VERSION:3.0
N:Vishwakarma;Hardik;;;
FN:Hardik Vishwakarma
TEL;TYPE=CELL:+916264600616
EMAIL:hello@hardik.me
END:VCARD`;

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    message: "",
    file: null,
  });

  const [showZoomHint, setShowZoomHint] = useState(false);
  const [scrollEnabled, setScrollEnabled] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form data submitted:", formData);
  };

  const MapScrollHandler = () => {
    useMapEvent("wheel", (e) => {
      if (!e.originalEvent.ctrlKey && !scrollEnabled) {
        setShowZoomHint(true);
        e.originalEvent.preventDefault();
      }
    });
    return null;
  };

  return (
   

<div
  ref={containerRef}
  className={`space-y-4 mb-10 md:mb-0 
    opacity-0 ${isVisible ? "blur-zoom-in" : ""}
  `}
>

      {/* Heading */}
      <h1 className="text-[28px] sm:text-[45px] font-semibold mt-[-20px] sm:mt-[-50px] ">
        Let's Connect
      </h1>
      <hr className="h-[4px] sm:h-[5px] bg-cyan-300 border-0 mt-3 w-[80px] sm:w-[100px]" />

      {/* Map Section */}
      <div className="w-full h-[270px] sm:h-[400px] rounded-2xl overflow-hidden shadow-lg relative">
        <MapContainer
          center={defaultPosition}
          zoom={15}
          style={{
            height: "100%",
            width: "100%",
            transition: "opacity 0.3s",
            opacity: showZoomHint && !scrollEnabled ? 0.5 : 1,
          }}
          zoomControl={false}
          scrollWheelZoom={scrollEnabled}
        >
          <ZoomControl position="bottomright" />
          <MapScrollHandler />

          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />

          <Marker position={defaultPosition} icon={customIcon}>
            <Popup>
              <div className="font-bold">{placeInfo.name}</div>
              <div>Rating: {placeInfo.rating} ⭐</div>
              <div>{placeInfo.address}</div>
              <a
                href={placeInfo.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
              >
                Directions
              </a>
            </Popup>
          </Marker>
        </MapContainer>

        {showZoomHint && !scrollEnabled && (
          <div
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center cursor-pointer"
            onClick={() => setScrollEnabled(true)}
          >
            <div className="bg-black/70 text-white px-6 py-3 rounded-lg text-lg font-semibold shadow-lg">
              Please click to enable scroll
            </div>
          </div>
        )}
      </div>

      {/* Contact Info Section */}
      <div className="
  flex flex-row justify-between items-start 
  bg-[#1f1f1f] border border-gray-700 rounded-2xl p-3 shadow-lg 
  gap-4 flex-wrap
">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
            Contact Details
          </h2>

          {/* Phone */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-1 md:p-3 bg-[#2d2d2d] rounded-xl text-cyan-500 border border-gray-600">
              <Phone size={22} />
            </div>
            <div>
              <p className="font-semibold text-white text-xs sm:text-base">Mobile</p>
              <a href="tel:+916264600616" className="text-gray-300 hover:text-cyan-400 text-xs sm:text-base">
                +91 6264600616
              </a>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center space-x-4">
            <div className="p-1 md:p-3 bg-[#2d2d2d] rounded-xl text-cyan-500 border border-gray-600">
              <Mail size={22} />
            </div>
            <div>
              <p className="font-semibold text-white text-xs sm:text-base">Email</p>
              <a href="mailto:hello@hardik.me" className="text-gray-300 hover:text-cyan-400 text-xs sm:text-base">
                hello@hardik.me
              </a>
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-gray-200 p-2 mt-12 md:mt-1 rounded-lg shadow-md self-center sm:self-auto">
          <QRCodeCanvas value={vCardData} size={qrSize} className="   sm:size-140" />
        </div>
      </div>










      {/* Contact Form */}
      <div className="
        w-full sm:w-[90%] md:w-[67rem] 
        bg-[#1f1f1f] border border-gray-700 rounded-2xl shadow-xl 
        p-3 sm:p-8 space-y-6 mx-auto
      ">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">Contact Form</h2>

        <form onSubmit={handleSubmit} className="space-y-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full p-3 sm:p-4 rounded-xl bg-[#2d2d2d] text-white placeholder-gray-400 border border-gray-600 focus:ring-2 focus:ring-cyan-500"
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 sm:p-4 rounded-xl bg-[#2d2d2d] text-white placeholder-gray-400 border border-gray-600 focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <textarea
            name="message"
            placeholder="Your Message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            className="w-full p-3 sm:p-4 rounded-xl bg-[#2d2d2d] text-white placeholder-gray-400 border border-gray-600 resize-none focus:ring-2 focus:ring-cyan-500"
          ></textarea>

          {/* File Upload */}
          <div className="flex flex-col items-start space-y-2">
            <label
              htmlFor="file-upload"
              className="
                flex items-center justify-center space-x-2 
                w-full p-3 sm:p-4 rounded-xl bg-[#2d2d2d] cursor-pointer 
                hover:bg-gray-700 border border-gray-600 text-cyan-400
              "
            >
              <FaPaperclip />
              <span>Attach PDF (Optional)</span>
            </label>

            <input id="file-upload" type="file" name="file" onChange={handleChange} className="hidden" accept=".pdf" />
            <span className="text-xs text-gray-500 ml-1">Only PDF files up to 1 MB</span>
          </div>

          {/* Bottom row */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">

            <p className="text-sm sm:text-base text-gray-500 text-center md:text-left leading-relaxed">
              This site is protected by reCAPTCHA and the Google  
              <a href="https://policies.google.com/privacy" target="_blank"  rel="noopener noreferrer               " className="text-cyan-400 hover:underline"> Privacy Policy </a>
              and  
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline"> Terms of Service </a>
              apply.
            </p>

            <button
              type="submit"
              className="flex items-center justify-center space-x-2 px-6 py-3 rounded-full bg-cyan-600 text-white font-medium hover:bg-cyan-700 transition-colors shadow-lg"
            >
              <FaPaperPlane />
              <span>Send Message</span>
            </button>

          </div>
        </form>
      </div>
    </div>
  );
};

export default Contact;

